import React, {createContext, useContext, useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {useHistory, useLocation} from 'react-router-dom'
import useEffectEvent from '../hooks/use-effect-event'
import useHydration from './hydration'

/* Product configurator state context - allows to obtain the current status of product configurator */
const ProductConfiguratorStateContext = createContext()
const ProductConfiguratorStateProvider = ({
    children,
    configuratorConfigId,
    initialStep,
    isBackFromConfigurator,
    isConfiguratorActive
}) => {
    const ctx = useMemo(
        () => ({configuratorConfigId, initialStep, isBackFromConfigurator, isConfiguratorActive}),
        [configuratorConfigId, initialStep, isBackFromConfigurator, isConfiguratorActive]
    )
    return (
        <ProductConfiguratorStateContext.Provider value={ctx}>
            {children}
        </ProductConfiguratorStateContext.Provider>
    )
}
ProductConfiguratorStateProvider.propTypes = {
    children: PropTypes.node,
    configuratorConfigId: PropTypes.string,
    initialStep: PropTypes.string,
    isBackFromConfigurator: PropTypes.bool,
    isConfiguratorActive: PropTypes.bool
}
/**
 * Obtain product configurator state
 * @returns {{
 *   configuratorConfigId: string,
 *   initialStep: string,
 *   isBackFromConfigurator: boolean,
 *   isConfiguratorActive: boolean,
 * }} - true if the product configurator is active
 */
export const useProductConfiguratorState = () => {
    const ctx = useContext(ProductConfiguratorStateContext)
    if (ctx == null) {
        throw new Error(
            'useProductConfiguratorState must be used within a ProductConfiguratorStateProvider'
        )
    }
    return ctx
}

/* Product configurator control context - allows to activate/deactivate product configurator */
const ProductConfiguratorControlContext = createContext()
const ProductConfiguratorControlProvider = ({children, startConfigurator, stopConfigurator}) => {
    const ctx = useMemo(
        () => ({startConfigurator, stopConfigurator}),
        [startConfigurator, stopConfigurator]
    )
    return (
        <ProductConfiguratorControlContext.Provider value={ctx}>
            {children}
        </ProductConfiguratorControlContext.Provider>
    )
}
ProductConfiguratorControlProvider.propTypes = {
    children: PropTypes.node,
    startConfigurator: PropTypes.func,
    stopConfigurator: PropTypes.func
}
/**
 * Obtain product configurator control
 * @returns {{
 *   startConfigurator: function(configuratorConfigId: string): void,
 *   stopConfigurator: function(): void
 * }} - functions to start/stop the product configurator
 */
export const useProductConfiguratorControl = () => {
    const ctx = useContext(ProductConfiguratorControlContext)
    if (!ctx) {
        throw new Error(
            'useProductConfiguratorControl must be used within a ProductConfiguratorControlProvider'
        )
    }
    return ctx
}

export const ProductConfiguratorProvider = ({
    children,
    configuratorConfigId,
    initialStep,
    isBackFromConfigurator,
    isConfiguratorActive,
    startConfigurator,
    stopConfigurator
}) => {
    return (
        <ProductConfiguratorControlProvider
            startConfigurator={startConfigurator}
            stopConfigurator={stopConfigurator}
        >
            <ProductConfiguratorStateProvider
                configuratorConfigId={configuratorConfigId}
                initialStep={initialStep}
                isConfiguratorActive={isConfiguratorActive}
                isBackFromConfigurator={isBackFromConfigurator}
            >
                {children}
            </ProductConfiguratorStateProvider>
        </ProductConfiguratorControlProvider>
    )
}

ProductConfiguratorProvider.propTypes = {
    children: PropTypes.node,
    configuratorConfigId: PropTypes.string,
    initialStep: PropTypes.string,
    isBackFromConfigurator: PropTypes.bool,
    isConfiguratorActive: PropTypes.bool,
    startConfigurator: PropTypes.func,
    stopConfigurator: PropTypes.func
}

/**
 * Provides product configurator state management and context provider
 * @returns {{
 *   configuratorConfigId: string,
 *   initialStep: string,
 *   isConfiguratorActive: boolean,
 *   startConfigurator: function (configuratorConfigId: string): void,
 *   stopConfigurator: function (): void,
 * }
 */
export const useProductConfigurator = () => {
    const history = useHistory()
    const location = useLocation()
    const isHydrated = useHydration()

    // this is a quick workaround to hide header, newsletter and footer,
    // when showing product configurator
    // use the history state to not expose the parameter in the url, but still save it in the history
    // note: need to wait for hydration to complete or will render different html than ssr
    const configuratorConfigId = isHydrated ? location?.state?.configuratorConfigId : undefined
    const initialStep = isHydrated ? location?.state?.initialStep : undefined
    const isConfiguratorActive = configuratorConfigId != null
    const isBackFromConfigurator = Boolean(location?.state?.backFromConfigurator)
    const startConfigurator = useEffectEvent((configuratorConfigId, extraState = {}) => {
        if (typeof configuratorConfigId !== 'string' || !configuratorConfigId) {
            throw new Error('Starting configurator requires a valid configuratorConfigId')
        }
        // remove the url configuration for back navigation
        history.replace({
            pathname: location.pathname,
            state: {
                ...location.state,
                backFromConfigurator: true
            }
        })
        // push the configurator state with optional url configuration
        history.push({
            pathname: location.pathname,
            search: location.search,
            state: {
                ...location.state,
                configuratorConfigId,
                ...extraState
            }
        })
    })
    const stopConfigurator = useEffectEvent(() => {
        // first use replace action because it is sync and allows for quick update of the UI
        // then the next action will trigger goBack to discard this history state
        history.replace({
            pathname: location.pathname,
            search: location.search,
            state: {
                ...location.state,
                configuratorConfigId: null
            }
        })
    })

    // go back the history stack if we are on a closed configurator state
    // this state only happens after closing configurator, and we know that the previous is
    // the pdp without configurator state
    const historyGoBack = history.goBack
    useEffect(() => {
        if (location?.state?.configuratorConfigId === null) {
            historyGoBack()
        }
    }, [location?.state?.configuratorConfigId, historyGoBack])

    return {
        configuratorConfigId,
        initialStep,
        isConfiguratorActive,
        startConfigurator,
        stopConfigurator,
        isBackFromConfigurator
    }
}
